import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from '@/shared/auth';
import { PRIVILEGE_CATALOG_VERSION } from '@stayon/auth-core';

export function SessionBootstrap({ children }: { children: ReactNode }) {
  const { hydrate, logout, status, user, supportResumeProblem, continueWithoutSupport } = useAuth();
  const retry = useCallback(() => void hydrate(), [hydrate]);
  const automaticRetries = useRef(0);

  useEffect(() => {
    if (status === 'idle') retry();
  }, [retry, status]);

  const catalogIsCurrent =
    !user ||
    user.principalKind === 'guest' ||
    user.privilegeCatalogVersion === PRIVILEGE_CATALOG_VERSION;
  const reloadKey = 'stayon.privilege-catalog-reload';

  useEffect(() => {
    if (catalogIsCurrent || status !== 'authenticated') return;
    if (sessionStorage.getItem(reloadKey) === PRIVILEGE_CATALOG_VERSION) return;
    sessionStorage.setItem(reloadKey, PRIVILEGE_CATALOG_VERSION);
    const url = new URL(window.location.href);
    url.searchParams.set('privilege-catalog', PRIVILEGE_CATALOG_VERSION);
    window.location.replace(url.toString());
  }, [catalogIsCurrent, status]);

  useEffect(() => {
    if (status !== 'reconnecting') return;
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [retry, status]);

  useEffect(() => {
    if (status === 'authenticated' || status === 'unauthenticated') {
      automaticRetries.current = 0;
      return;
    }
    if (status !== 'reconnecting' || automaticRetries.current >= 2) return;
    const baseDelay = automaticRetries.current === 0 ? 1_500 : 5_000;
    const delay = baseDelay + Math.floor(Math.random() * 250);
    const timer = window.setTimeout(() => {
      automaticRetries.current += 1;
      retry();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [retry, status]);

  if (!catalogIsCurrent && sessionStorage.getItem(reloadKey) === PRIVILEGE_CATALOG_VERSION) {
    return (
      <main className="bg-paper text-ink flex min-h-screen items-center justify-center p-6">
        <section className="border-line w-full max-w-sm rounded-xl border bg-white p-6 text-center shadow-sm">
          <p className="font-serif text-xl">Sicherheitsstand wurde aktualisiert</p>
          <p className="text-ink-soft mt-2 text-sm">
            Bitte melden Sie sich erneut an, damit der aktuelle Zugriffsstand geladen wird.
          </p>
          <button
            type="button"
            onClick={() => void logout()}
            className="bg-sage focus-visible:ring-sage mt-5 min-h-11 rounded-md px-5 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            Abmelden und neu anmelden
          </button>
        </section>
      </main>
    );
  }

  if (user || status === 'authenticated' || status === 'unauthenticated') {
    return children;
  }

  return (
    <main className="bg-paper text-ink flex min-h-screen items-center justify-center p-6">
      <section className="border-line w-full max-w-sm rounded-xl border bg-white p-6 text-center shadow-sm">
        <p className="font-serif text-xl">Sitzung wird wiederhergestellt</p>
        <p className="text-ink-soft mt-2 text-sm" role="status">
          {status !== 'reconnecting'
            ? 'Einen Moment bitte.'
            : supportResumeProblem
              ? `Die Support-Sitzung dieses Tabs ließ sich nicht wiederherstellen. ${supportResumeProblem}`
              : 'Die Verbindung ist unterbrochen. Wir versuchen es automatisch erneut.'}
        </p>
        {status === 'reconnecting' && (
          <div className="mt-5 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={retry}
              className="bg-sage focus-visible:ring-sage min-h-11 rounded-md px-5 text-sm font-semibold text-white focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              Erneut versuchen
            </button>
            {supportResumeProblem && (
              <button
                type="button"
                onClick={continueWithoutSupport}
                className="border-line text-ink focus-visible:ring-sage min-h-11 rounded-md border px-5 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                Ohne Support-Sitzung fortfahren
              </button>
            )}
          </div>
        )}
        {status === 'reconnecting' && supportResumeProblem && (
          <p className="text-ink-soft mt-2 text-xs">
            Sie arbeiten dann in Ihrem eigenen Workspace; beim Server endet die Sitzung spätestens
            mit ihrem Ablauf.
          </p>
        )}
      </section>
    </main>
  );
}
